import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  Play, Loader2, RefreshCw, Search, Building2, Database, 
  Activity, Clock, CheckCircle2, AlertCircle, TestTube, 
  RotateCcw, Filter, Globe, BarChart3
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CompanyProfilesPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Run config
  const [maxParts, setMaxParts] = useState(3);
  const [limitRows, setLimitRows] = useState(100);
  
  // Search filters
  const [searchQuery, setSearchQuery] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  
  // Dropdown options
  const [sectors, setSectors] = useState([]);
  const [industries, setIndustries] = useState([]);
  
  // Pagination
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 50;

  useEffect(() => {
    fetchStats();
    fetchSectors();
  }, [token]);

  useEffect(() => {
    if (activeTab === "browse") {
      fetchProfiles();
    }
  }, [activeTab, searchQuery, sectorFilter, industryFilter, typeFilter, offset]);

  useEffect(() => {
    if (sectorFilter) {
      fetchIndustries(sectorFilter);
    } else {
      setIndustries([]);
      setIndustryFilter("");
    }
  }, [sectorFilter]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/company-profiles/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSectors = async () => {
    try {
      const response = await fetch(`${API}/company-profiles/sectors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSectors(data.sectors || []);
      }
    } catch (error) {
      console.error("Failed to fetch sectors:", error);
    }
  };

  const fetchIndustries = async (sector) => {
    try {
      const response = await fetch(`${API}/company-profiles/industries?sector=${encodeURIComponent(sector)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setIndustries(data.industries || []);
      }
    } catch (error) {
      console.error("Failed to fetch industries:", error);
    }
  };

  const fetchProfiles = useCallback(async () => {
    try {
      let url = `${API}/company-profiles/search?limit=${limit}&offset=${offset}`;
      if (searchQuery) url += `&q=${encodeURIComponent(searchQuery)}`;
      if (sectorFilter) url += `&sector=${encodeURIComponent(sectorFilter)}`;
      if (industryFilter) url += `&industry=${encodeURIComponent(industryFilter)}`;
      if (typeFilter === "etf") url += `&is_etf=true`;
      if (typeFilter === "fund") url += `&is_fund=true`;
      if (typeFilter === "adr") url += `&is_adr=true`;
      if (typeFilter === "stock") url += `&is_stock=true`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProfiles(data.profiles || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error("Failed to fetch profiles:", error);
    }
  }, [token, searchQuery, sectorFilter, industryFilter, typeFilter, offset]);

  // Run actions
  const runFullRefresh = async () => {
    if (!window.confirm("This will re-fetch ALL company profiles from FMP. This may take several minutes. Continue?")) {
      return;
    }
    setRunning(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 min timeout
      
      toast.info("Starting full refresh... This may take several minutes.");
      
      const response = await fetch(`${API}/company-profiles/run?force_full_refresh=true`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const result = await response.json();
        toast.success(
          `Synced ${result.profiles_upserted} profiles from ${result.parts_processed.length} parts`
        );
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to run sync");
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        toast.error("Request timed out. Check the pipeline state for progress.");
      } else {
        toast.error("Failed to run sync: " + (error.message || "Network error"));
      }
    } finally {
      setRunning(false);
    }
  };

  const runResume = async () => {
    setRunning(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 min timeout
      
      toast.info("Resuming sync... This may take a minute.");
      
      const response = await fetch(`${API}/company-profiles/run?force_full_refresh=false`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const result = await response.json();
        toast.success(
          `Synced ${result.profiles_upserted} profiles from ${result.parts_processed.length} parts`
        );
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to run sync");
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        toast.error("Request timed out. Check the pipeline state for progress.");
      } else {
        toast.error("Failed to run sync: " + (error.message || "Network error"));
      }
    } finally {
      setRunning(false);
    }
  };

  const runTest = async () => {
    setRunning(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 min timeout
      
      toast.info("Running test sync...");
      
      const response = await fetch(
        `${API}/company-profiles/run-test?max_parts=${maxParts}&limit_rows=${limitRows}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        }
      );
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const result = await response.json();
        toast.success(
          `Test sync: ${result.profiles_upserted} profiles from ${result.parts_processed.length} parts`
        );
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to run test sync");
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        toast.error("Request timed out. Try with fewer parts.");
      } else {
        toast.error("Failed to run test sync: " + (error.message || "Network error"));
      }
    } finally {
      setRunning(false);
    }
  };

  const resetState = async () => {
    if (!window.confirm("Reset pipeline state? Next run will start from part 0.")) {
      return;
    }
    try {
      const response = await fetch(`${API}/company-profiles/reset-state`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success("Pipeline state reset");
        fetchStats();
      } else {
        toast.error("Failed to reset state");
      }
    } catch (error) {
      toast.error("Failed to reset state");
    }
  };

  const formatNumber = (val) => {
    if (val === null || val === undefined) return "0";
    return val.toLocaleString("en-US");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-profiles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Company Profiles</h1>
          <p className="text-sm text-muted-foreground">
            FMP company reference data (symbols, sectors, industries)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={runResume}
            disabled={running}
            className="rounded-sm"
            data-testid="resume-sync-btn"
          >
            {running ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Resume Sync
          </Button>
          <Button
            onClick={runFullRefresh}
            disabled={running}
            className="rounded-sm"
            data-testid="full-refresh-btn"
          >
            {running ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Full Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Profiles</p>
                <p className="text-2xl font-bold text-primary">
                  {formatNumber(stats?.total_profiles)}
                </p>
              </div>
              <Database className="w-8 h-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">With CIK</p>
                <p className="text-2xl font-bold text-green-400">
                  {formatNumber(stats?.with_cik)}
                </p>
              </div>
              <Building2 className="w-8 h-8 text-green-400/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">ETFs</p>
                <p className="text-2xl font-bold">{formatNumber(stats?.etf_count)}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Sectors</p>
                <p className="text-2xl font-bold">{formatNumber(stats?.sector_count)}</p>
              </div>
              <Globe className="w-8 h-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Industries</p>
                <p className="text-2xl font-bold">{formatNumber(stats?.industry_count)}</p>
              </div>
              <Activity className="w-8 h-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline State */}
      {stats?.state && (
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Pipeline State</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Last run:</span>
                <span>{formatDate(stats.state.last_run_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={stats.state.last_run_status === "completed" ? "default" : "secondary"}>
                  {stats.state.last_run_status || "idle"}
                </Badge>
              </div>
              {stats.state.last_part_processed !== null && (
                <div>
                  <span className="text-muted-foreground">Last part: </span>
                  <span className="font-mono">{stats.state.last_part_processed}</span>
                </div>
              )}
              {stats.state.last_run_duration_seconds && (
                <div>
                  <span className="text-muted-foreground">Duration: </span>
                  <span>{stats.state.last_run_duration_seconds}s</span>
                </div>
              )}
              {stats.state.error_count > 0 && (
                <Badge variant="destructive">{stats.state.error_count} errors</Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={resetState}
                className="rounded-sm text-orange-400 hover:text-orange-300"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Reset State
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted rounded-sm">
          <TabsTrigger value="overview" className="rounded-sm">Overview</TabsTrigger>
          <TabsTrigger value="browse" className="rounded-sm">
            <Search className="w-4 h-4 mr-1" />
            Browse
          </TabsTrigger>
          <TabsTrigger value="run-options" className="rounded-sm">
            <Play className="w-4 h-4 mr-1" />
            Run Options
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Quick Stats */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm">Data Coverage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <StatRow label="Profiles with CIK (13F linkable)" value={stats?.with_cik} total={stats?.total_profiles} />
                  <StatRow label="Profiles with CUSIP" value={stats?.with_cusip} total={stats?.total_profiles} />
                  <StatRow label="Actively Trading" value={stats?.actively_trading} total={stats?.total_profiles} />
                  <StatRow label="ETFs" value={stats?.etf_count} total={stats?.total_profiles} />
                  <StatRow label="Funds" value={stats?.fund_count} total={stats?.total_profiles} />
                  <StatRow label="ADRs" value={stats?.adr_count} total={stats?.total_profiles} />
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm">Quick Actions</CardTitle>
                <CardDescription>
                  Manage company profile sync pipeline
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    onClick={runResume}
                    disabled={running}
                    className="w-full justify-start rounded-sm"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Resume Sync (from last part)
                  </Button>
                  <Button
                    variant="outline"
                    onClick={runFullRefresh}
                    disabled={running}
                    className="w-full justify-start rounded-sm"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Full Refresh (all parts)
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setActiveTab("run-options")}
                    className="w-full justify-start rounded-sm"
                  >
                    <TestTube className="w-4 h-4 mr-2" />
                    Test Mode / Targeted Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Browse Tab */}
        <TabsContent value="browse" className="mt-4">
          <Card className="bg-card border-border rounded-sm">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <CardTitle className="text-sm">Company Profiles</CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    placeholder="Search symbol/name..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setOffset(0); }}
                    className="w-40 bg-background rounded-sm text-xs"
                  />
                  <Select value={sectorFilter || "all"} onValueChange={(v) => { setSectorFilter(v === "all" ? "" : v); setOffset(0); }}>
                    <SelectTrigger className="w-36 bg-background rounded-sm text-xs">
                      <SelectValue placeholder="Sector" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sectors</SelectItem>
                      {sectors.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {industries.length > 0 && (
                    <Select value={industryFilter || "all"} onValueChange={(v) => { setIndustryFilter(v === "all" ? "" : v); setOffset(0); }}>
                      <SelectTrigger className="w-36 bg-background rounded-sm text-xs">
                        <SelectValue placeholder="Industry" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Industries</SelectItem>
                        {industries.map(i => (
                          <SelectItem key={i} value={i}>{i}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Select value={typeFilter || "all"} onValueChange={(v) => { setTypeFilter(v === "all" ? "" : v); setOffset(0); }}>
                    <SelectTrigger className="w-28 bg-background rounded-sm text-xs">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="stock">Stocks</SelectItem>
                      <SelectItem value="etf">ETFs</SelectItem>
                      <SelectItem value="fund">Funds</SelectItem>
                      <SelectItem value="adr">ADRs</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchQuery("");
                      setSectorFilter("");
                      setIndustryFilter("");
                      setTypeFilter("");
                      setOffset(0);
                    }}
                    className="rounded-sm"
                  >
                    <Filter className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left py-2">Symbol</th>
                      <th className="text-left py-2">Company Name</th>
                      <th className="text-left py-2">Sector</th>
                      <th className="text-left py-2">Industry</th>
                      <th className="text-left py-2">Exchange</th>
                      <th className="text-left py-2">Type</th>
                      <th className="text-center py-2">Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profiles.map((profile, idx) => {
                      // Determine the type badge
                      const getTypeBadge = () => {
                        if (profile.is_etf) return <Badge variant="outline" className="text-xs">ETF</Badge>;
                        if (profile.is_fund) return <Badge variant="outline" className="text-xs">Fund</Badge>;
                        if (profile.is_adr) return <Badge variant="outline" className="text-xs">ADR</Badge>;
                        return <Badge variant="outline" className="text-xs text-muted-foreground">Stock</Badge>;
                      };
                      
                      return (
                        <tr key={idx} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="py-2 font-mono">{profile.symbol}</td>
                          <td className="py-2 text-xs max-w-[200px] truncate">
                            {profile.company_name || "-"}
                          </td>
                          <td className="py-2 text-xs text-muted-foreground">
                            {profile.sector || "-"}
                          </td>
                          <td className="py-2 text-xs text-muted-foreground">
                            {profile.industry || "-"}
                          </td>
                          <td className="py-2 text-xs">
                            {profile.exchange || "-"}
                          </td>
                          <td className="py-2">
                            {getTypeBadge()}
                          </td>
                          <td className="py-2 text-center">
                            {profile.is_actively_trading ? (
                              <span className="inline-flex items-center justify-center w-5 h-5 bg-green-500/20 rounded-full">
                                <span className="w-2 h-2 bg-green-400 rounded-full" />
                              </span>
                            ) : (
                              <span className="inline-flex items-center justify-center w-5 h-5 bg-red-500/20 rounded-full">
                                <span className="w-2 h-2 bg-red-400 rounded-full" />
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ScrollArea>
              
              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 text-sm">
                <span className="text-muted-foreground">
                  Showing {offset + 1}-{Math.min(offset + limit, total)} of {formatNumber(total)}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                    className="rounded-sm"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOffset(offset + limit)}
                    disabled={offset + limit >= total}
                    className="rounded-sm"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Run Options Tab */}
        <TabsContent value="run-options" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Test Mode */}
            <Card className="bg-card border-border rounded-sm">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <TestTube className="w-4 h-4 text-yellow-400" />
                  Test Mode
                </CardTitle>
                <CardDescription>
                  Run with limited parts and rows for testing
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground">Max Parts</label>
                      <Input
                        type="number"
                        value={maxParts}
                        onChange={(e) => setMaxParts(parseInt(e.target.value) || 3)}
                        className="bg-background rounded-sm"
                        min={1}
                        max={10}
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground">Limit Rows/Part</label>
                      <Input
                        type="number"
                        value={limitRows}
                        onChange={(e) => setLimitRows(parseInt(e.target.value) || 100)}
                        className="bg-background rounded-sm"
                        min={1}
                        max={1000}
                      />
                    </div>
                  </div>
                  <Button
                    onClick={runTest}
                    disabled={running}
                    className="w-full rounded-sm"
                    data-testid="run-test-btn"
                  >
                    {running ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <TestTube className="w-4 h-4 mr-2" />
                    )}
                    Run Test Sync
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Stat Row Component
const StatRow = ({ label, value, total }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm">{value?.toLocaleString() || 0}</span>
        {total && (
          <Badge variant="outline" className="text-xs">
            {pct}%
          </Badge>
        )}
      </div>
    </div>
  );
};

export default CompanyProfilesPage;
